import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";

import step1Image from "../assets/img/step1-personal.png";
import step2Image from "../assets/img/step2-documents.png";
import step3Image from "../assets/img/step3-security.png";

/* =========================================================
   CONSTANTS / UTILS
========================================================= */

const regions = ["Martinique", "Mayotte", "La Réunion", "Guyane Française"];
const europeCountries = [
  "Allemagne","Autriche","Belgique","Bulgarie","Chypre","Croatie","Danemark","Espagne","Estonie","Finlande","France","Grèce",
  "Hongrie","Irlande","Islande","Italie","Lettonie","Lituanie","Luxembourg","Malte","Norvège","Pays-Bas","Pologne","Portugal",
  "République Tchèque","Roumanie","Royaume-Uni","Slovaquie","Slovénie","Suède","Suisse",
];
const paysOptions = [...regions, ...europeCountries];
const devises = ["EUR", "USD", "GBP", "CHF"];
const emailRegex = /\S+@\S+\.\S+/;

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/* =========================================================
   ✅ IMPORTANT: UI COMPONENTS MUST BE OUTSIDE
   (sinon ils changent d'identité à chaque render -> focus saute)
========================================================= */

const StepPill = React.memo(function StepPill({ icon, label, active }) {
  return (
    <div className={`stepPill ${active ? "active" : ""}`}>
      <span className="stepPillIcon">{icon}</span>
      <span className="stepPillLabel">{label}</span>
    </div>
  );
});

const Badge = React.memo(function Badge({ children, tone = "brand" }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
});

const Field = React.memo(function Field({ label, required, error, htmlFor, children }) {
  return (
    <div className="field">
      <div className="fieldTop">
        {/* ✅ label associé */}
        <label className="label" htmlFor={htmlFor}>
          {label}
          {required ? <span className="req"> *</span> : null}
        </label>
        {error ? <span className="errorTiny">{error}</span> : null}
      </div>
      {children}
    </div>
  );
});

/** Section pliable (optionnelle) */
const Collapsible = React.memo(function Collapsible({ title, right, open, onToggle, children }) {
  return (
    <div className={`collapsible ${open ? "open" : ""}`}>
      <button type="button" className="collapsibleBtn" onClick={onToggle} aria-expanded={open}>
        <div className="collapsibleLeft">
          <span className="chev">{open ? "▾" : "▸"}</span>
          <span className="collapsibleTitle">{title}</span>
        </div>
        <div className="collapsibleRight">{right}</div>
      </button>
      {open ? <div className="collapsibleBody">{children}</div> : null}
    </div>
  );
});

const UploadCard = React.memo(function UploadCard({
  title,
  hint,
  file,
  previewUrl,
  error,
  onPick,
  onRemove,
  accept = "image/jpeg,image/png,image/jpg",
  id,
}) {
  return (
    <div className={`uploadCard ${error ? "uploadCard--error" : ""}`}>
      {!file ? (
        <label className="dropzone" htmlFor={id}>
          <div className="dropTop">
            <span className="dropIcon">
              <UploadSvg />
            </span>
            <div className="dropText">
              <div className="uploadTitle">{title}</div>
              <div className="uploadHint">{hint}</div>
            </div>
          </div>
          <div className="dropFooter">
            <Badge tone="muted">JPG / PNG</Badge>
            <Badge tone="muted">Max 10 Mo</Badge>
          </div>
          {/* ✅ input relié au label via id */}
          <input
            id={id}
            type="file"
            accept={accept}
            onChange={onPick}
            style={{ display: "none" }}
          />
        </label>
      ) : (
        <div className="previewWrap">
          <img src={previewUrl || ""} alt="Aperçu" className="previewImg" />
          <div className="previewMeta">
            <div className="previewName">{file.name}</div>
            <div className="previewSize">{formatBytes(file.size)}</div>
          </div>
          <button type="button" className="removeBtn" onClick={onRemove}>
            <TrashSvg />
            Supprimer
          </button>
        </div>
      )}

      {error ? <div className="errorBlock">{error}</div> : null}
    </div>
  );
});

/* =========================================================
   MAIN COMPONENT
========================================================= */

const MultiStepForm = () => {
  const history = useHistory();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formErrors, setFormErrors] = useState({});
  const [erreur, setErreur] = useState("");

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    dateNaissance: "",
    telephone: "",
    montantDemande: "",
    projetDescription: "",
    pays: "",
    ville: "",
    devise: "",
    mdp: "",
    adresse: "",
    sexe: "",
    typeAide: "",
    statut: "En attente",
  });

  const [files, setFiles] = useState({
    idcard1: null,
    idcard2: null,
    homeproof1: null,
    homeproof2: null,
  });

  const [previews, setPreviews] = useState({
    idcard1: null,
    idcard2: null,
    homeproof1: null,
    homeproof2: null,
  });

  // ✅ Sections pliables (facultatif)
  const [openSections, setOpenSections] = useState({
    personal: true,
    contact: true,
    address: true,
    project: true,
    identity: true,
    homeproof: true,
    password: true,
    recap: true,
  });

  const inputRefs = useRef({});

  const today = useMemo(() => new Date(), []);
  const maxDateObj = useMemo(
    () => new Date(today.getFullYear() - 16, today.getMonth(), today.getDate()),
    [today]
  );
  const maxDate = useMemo(() => maxDateObj.toISOString().split("T")[0], [maxDateObj]);

  const stepImages = useMemo(() => [step1Image, step2Image, step3Image], []);
  const stepTitles = useMemo(
    () => ["Informations personnelles", "Projet & Documents", "Sécurité"],
    []
  );

  const [imgOk, setImgOk] = useState([true, true, true]);
  const currentImg = stepImages[currentStep - 1];
  const currentImgOk = imgOk[currentStep - 1];

  // cleanup previews
  useEffect(() => {
    return () => {
      Object.values(previews).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const checkEmailAvailability = async (email) => {
    try {
      const response = await axios.post(
        "https://appapi.unpaidfinancial.com/api/check-email",
        { email }
      );
      return !!response.data?.available;
    } catch {
      return true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (prev[name] === value) return prev;
      return { ...prev, [name]: value };
    });

    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      return { ...prev, [name]: "" };
    });
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: "Le fichier ne doit pas dépasser 10 Mo",
      }));
      return;
    }
    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: "Seuls JPG et PNG sont acceptés",
      }));
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    if (previews[fieldName]) URL.revokeObjectURL(previews[fieldName]);

    setFiles((prev) => ({ ...prev, [fieldName]: file }));
    setPreviews((prev) => ({ ...prev, [fieldName]: previewUrl }));
    setFormErrors((prev) => ({ ...prev, [fieldName]: "" }));
  };

  const handleRemoveFile = (fieldName) => {
    if (previews[fieldName]) URL.revokeObjectURL(previews[fieldName]);
    setFiles((prev) => ({ ...prev, [fieldName]: null }));
    setPreviews((prev) => ({ ...prev, [fieldName]: null }));
    setFormErrors((prev) => ({ ...prev, [fieldName]: "" }));
  };

  const focusFirstError = (errors) => {
    const first = Object.keys(errors)[0];
    if (first && inputRefs.current[first]) {
      inputRefs.current[first].focus();
    }
  };

  const validateStep1 = async () => {
    const errors = {};

    if (!formData.nom?.trim()) errors.nom = "Nom obligatoire.";
    if (!formData.prenom?.trim()) errors.prenom = "Prénom obligatoire.";

    if (!formData.dateNaissance) errors.dateNaissance = "Date de naissance obligatoire.";
    else {
      const naissance = new Date(formData.dateNaissance);
      if (isNaN(naissance.getTime()) || naissance > maxDateObj) {
        errors.dateNaissance = "Vous devez avoir au moins 16 ans.";
      }
    }

    if (!formData.sexe) errors.sexe = "Genre obligatoire.";

    if (!formData.email?.trim()) errors.email = "Email obligatoire.";
    else if (!emailRegex.test(formData.email)) errors.email = "Email invalide.";

    if (!formData.telephone?.trim()) errors.telephone = "Téléphone obligatoire.";

    if (!formData.pays) errors.pays = "Pays obligatoire.";
    if (!formData.ville?.trim()) errors.ville = "Ville obligatoire.";
    if (!formData.adresse?.trim()) errors.adresse = "Adresse obligatoire.";

    if (!errors.email && formData.email) {
      const ok = await checkEmailAvailability(formData.email);
      if (!ok) errors.email = "Cet email est déjà utilisé.";
    }

    setFormErrors(errors);
    focusFirstError(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};

    if (!formData.typeAide) errors.typeAide = "Type d'aide obligatoire.";
    if (!formData.montantDemande) errors.montantDemande = "Montant obligatoire.";
    else if (Number(formData.montantDemande) <= 0) errors.montantDemande = "Montant invalide.";
    if (!formData.devise) errors.devise = "Devise obligatoire.";
    if (!formData.projetDescription?.trim()) errors.projetDescription = "Description obligatoire.";

    if (!files.idcard1) errors.idcard1 = "Au moins une photo de pièce d'identité obligatoire.";
    if (!files.homeproof1) errors.homeproof1 = "Au moins une photo de justificatif obligatoire.";

    setFormErrors(errors);
    focusFirstError(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep3 = () => {
    const errors = {};
    if (!formData.mdp) errors.mdp = "Mot de passe obligatoire.";
    else if (formData.mdp.length < 6) errors.mdp = "Minimum 6 caractères.";

    setFormErrors(errors);
    focusFirstError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    setErreur("");

    if (currentStep === 1) {
      const ok = await validateStep1();
      if (!ok) return;
    }
    if (currentStep === 2) {
      const ok = validateStep2();
      if (!ok) return;
    }

    setCurrentStep((prev) => clamp(prev + 1, 1, 3));
    setFormErrors({});
  };

  const handlePrevious = () => {
    setErreur("");
    setCurrentStep((prev) => clamp(prev - 1, 1, 3));
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur("");

    const ok = validateStep3();
    if (!ok) return;

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === "montantDemande") {
          const parsed = parseInt(value || "0", 10);
          formDataToSend.append(key, Number.isFinite(parsed) ? parsed.toString() : "0");
        } else {
          formDataToSend.append(key, value ?? "");
        }
      });

      if (files.idcard1) formDataToSend.append("idcard", files.idcard1);
      if (files.idcard2) formDataToSend.append("idcard2", files.idcard2);
      if (files.homeproof1) formDataToSend.append("homeproof", files.homeproof1);
      if (files.homeproof2) formDataToSend.append("homeproof2", files.homeproof2);

      await axios.post("https://appapi.unpaidfinancial.com/api/register", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      history.push("/login");
    } catch {
      setErreur("Erreur lors de l'enregistrement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <style>{css}</style>

      <div className="loginBg" aria-hidden="true" />

      <div className="shell">
        {/* LEFT */}
        <aside className="hero">
          <div className="heroTop">
            <div className="logo">
              <span className="logoMark">
                <BoltSvg />
              </span>
              <span className="logoText">AidFoundation</span>
            </div>

            <div className="heroKicker">Demande d’aide — Processus sécurisé</div>
            <h1 className="heroTitle">Finalisez votre demande en 3 étapes</h1>
            <p className="heroSub">
              Formulaire simple, clair et confortable à lire. Vous pouvez avancer étape par étape.
            </p>
          </div>

          <div className="heroSteps">
            <StepPill icon={<UserSvg />} label="Infos" active={currentStep === 1} />
            <StepPill icon={<DocSvg />} label="Documents" active={currentStep === 2} />
            <StepPill icon={<ShieldSvg />} label="Sécurité" active={currentStep === 3} />
          </div>

          <div className="heroCard">
            <div className="heroCardHeader">
              <span className="heroCardIcon">
                <WaveSvg />
              </span>
              <div>
                <div className="heroCardTitle">Astuce</div>
                <div className="heroCardText">
                  Préparez votre pièce d’identité et un justificatif de domicile : JPG/PNG, max 10 Mo.
                </div>
              </div>
            </div>

            <div className="heroStats">
              <div className="mini">
                <div className="miniValue">3</div>
                <div className="miniLabel">Étapes</div>
              </div>
              <div className="mini">
                <div className="miniValue">~2 min</div>
                <div className="miniLabel">Temps</div>
              </div>
              <div className="mini">
                <div className="miniValue">SSL</div>
                <div className="miniLabel">Sécurisé</div>
              </div>
            </div>
          </div>

          <div className="heroImage">
            <div className="heroImageFrame">
              {currentImgOk ? (
                <img
                  src={currentImg}
                  alt={`Illustration step ${currentStep}`}
                  className="heroImg"
                  loading="lazy"
                  onError={() =>
                    setImgOk((prev) => {
                      const next = [...prev];
                      next[currentStep - 1] = false;
                      return next;
                    })
                  }
                />
              ) : (
                <div className="heroImgFallback">
                  <div className="fallbackTitle">Illustration introuvable</div>
                  <div className="fallbackText">
                    Vérifie le chemin/nom : <code>../assets/img/step{currentStep}-*.png</code>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* RIGHT */}
        <main className="card">
          <div className="cardHeader">
            <div className="progress" aria-label="Progression">
              {[1, 2, 3].map((s) => {
                const isOn = currentStep >= s;
                const isDone = currentStep > s;
                const isActive = currentStep === s;

                return (
                  <div key={s} className="pStep">
                    <div className={`pDot ${isOn ? "on" : ""} ${isDone ? "done" : ""} ${isActive ? "active" : ""}`}>
                      {isDone ? "✓" : s}
                    </div>
                    <div className={`pLabel ${isOn ? "on" : ""}`}>{stepTitles[s - 1]}</div>
                    {s < 3 ? <div className={`pLine ${currentStep > s ? "on" : ""}`} /> : null}
                  </div>
                );
              })}
            </div>

            <div className="titleRow">
              <h2 className="stepTitle">{stepTitles[currentStep - 1]}</h2>
              <Badge tone="brand">Étape {currentStep}/3</Badge>
            </div>

            <div className="divider" />
          </div>

          <form onSubmit={handleSubmit} className="form" autoComplete="off">
            {/* ================= STEP 1 ================= */}
            {currentStep === 1 && (
              <div className="step stepIn">
                <Collapsible
                  title="Identité"
                  right={<Badge tone="muted">Obligatoire</Badge>}
                  open={openSections.personal}
                  onToggle={() => toggleSection("personal")}
                >
                  <div className="grid2">
                    <Field label="Nom" required error={formErrors.nom} htmlFor="nom">
                      <input
                        id="nom"
                        ref={(el) => (inputRefs.current.nom = el)}
                        className={`input ${formErrors.nom ? "bad" : ""}`}
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        placeholder="Votre nom"
                      />
                    </Field>

                    <Field label="Prénom" required error={formErrors.prenom} htmlFor="prenom">
                      <input
                        id="prenom"
                        ref={(el) => (inputRefs.current.prenom = el)}
                        className={`input ${formErrors.prenom ? "bad" : ""}`}
                        type="text"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        placeholder="Votre prénom"
                      />
                    </Field>
                  </div>

                  <div className="grid2">
                    <Field
                      label="Date de naissance"
                      required
                      error={formErrors.dateNaissance}
                      htmlFor="dateNaissance"
                    >
                      <input
                        id="dateNaissance"
                        ref={(el) => (inputRefs.current.dateNaissance = el)}
                        className={`input ${formErrors.dateNaissance ? "bad" : ""}`}
                        type="date"
                        name="dateNaissance"
                        max={maxDate}
                        value={formData.dateNaissance}
                        onChange={handleChange}
                      />
                      <div className="hint">Minimum 16 ans.</div>
                    </Field>

                    <Field label="Genre" required error={formErrors.sexe} htmlFor="sexe">
                      <select
                        id="sexe"
                        ref={(el) => (inputRefs.current.sexe = el)}
                        className={`input ${formErrors.sexe ? "bad" : ""}`}
                        name="sexe"
                        value={formData.sexe}
                        onChange={handleChange}
                      >
                        <option value="">Sélectionnez</option>
                        <option value="Masculin">Masculin</option>
                        <option value="Féminin">Féminin</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </Field>
                  </div>
                </Collapsible>

                <Collapsible
                  title="Contact"
                  right={<Badge tone="muted">Obligatoire</Badge>}
                  open={openSections.contact}
                  onToggle={() => toggleSection("contact")}
                >
                  <Field label="Email" required error={formErrors.email} htmlFor="email">
                    <input
                      id="email"
                      ref={(el) => (inputRefs.current.email = el)}
                      className={`input ${formErrors.email ? "bad" : ""}`}
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="exemple@domaine.com"
                      inputMode="email"
                      autoComplete="new-email"
                    />
                  </Field>

                  <Field label="Téléphone" required error={formErrors.telephone} htmlFor="telephone">
                    <input
                      id="telephone"
                      ref={(el) => (inputRefs.current.telephone = el)}
                      className={`input ${formErrors.telephone ? "bad" : ""}`}
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      placeholder="+33 6 00 00 00 00"
                      inputMode="tel"
                      autoComplete="new-tel"
                    />
                  </Field>
                </Collapsible>

                <Collapsible
                  title="Adresse"
                  right={<Badge tone="muted">Obligatoire</Badge>}
                  open={openSections.address}
                  onToggle={() => toggleSection("address")}
                >
                  <Field label="Pays / Région" required error={formErrors.pays} htmlFor="pays">
                    <select
                      id="pays"
                      ref={(el) => (inputRefs.current.pays = el)}
                      className={`input ${formErrors.pays ? "bad" : ""}`}
                      name="pays"
                      value={formData.pays}
                      onChange={handleChange}
                    >
                      <option value="">Sélectionnez</option>
                      {paysOptions.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <div className="grid2">
                    <Field label="Ville" required error={formErrors.ville} htmlFor="ville">
                      <input
                        id="ville"
                        ref={(el) => (inputRefs.current.ville = el)}
                        className={`input ${formErrors.ville ? "bad" : ""}`}
                        type="text"
                        name="ville"
                        value={formData.ville}
                        onChange={handleChange}
                        placeholder="Votre ville"
                      />
                    </Field>

                    <Field label="Adresse résidentielle" required error={formErrors.adresse} htmlFor="adresse">
                      <input
                        id="adresse"
                        ref={(el) => (inputRefs.current.adresse = el)}
                        className={`input ${formErrors.adresse ? "bad" : ""}`}
                        type="text"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        placeholder="Rue, numéro, complément…"
                      />
                    </Field>
                  </div>
                </Collapsible>
              </div>
            )}

            {/* ================= STEP 2 ================= */}
            {currentStep === 2 && (
              <div className="step stepIn">
                <Collapsible
                  title="Projet"
                  right={<Badge tone="muted">Obligatoire</Badge>}
                  open={openSections.project}
                  onToggle={() => toggleSection("project")}
                >
                  <Field label="Type d'aide" required error={formErrors.typeAide} htmlFor="typeAide">
                    <select
                      id="typeAide"
                      ref={(el) => (inputRefs.current.typeAide = el)}
                      className={`input ${formErrors.typeAide ? "bad" : ""}`}
                      name="typeAide"
                      value={formData.typeAide}
                      onChange={handleChange}
                    >
                      <option value="">Sélectionnez</option>
                      <option>Aides</option>
                      <option>Aide financière non remboursable</option>
                      <option>Bourse familiale</option>
                      <option>Aides aux personnes âgées</option>
                      <option>Soutiens financier à la création</option>
                      <option>Aide financière non remboursable avec assurances</option>
                      <option>Bourse familiale avec assurances</option>
                      <option>Aides aux personnes âgées avec assurances</option>
                      <option>Soutiens financier à la création avec assurances</option>
                    </select>
                  </Field>

                  <div className="grid2">
                    <Field label="Montant demandé" required error={formErrors.montantDemande} htmlFor="montantDemande">
                      <input
                        id="montantDemande"
                        ref={(el) => (inputRefs.current.montantDemande = el)}
                        className={`input ${formErrors.montantDemande ? "bad" : ""}`}
                        type="number"
                        min="1"
                        name="montantDemande"
                        value={formData.montantDemande}
                        onChange={handleChange}
                        placeholder="Ex: 2500"
                        inputMode="numeric"
                      />
                    </Field>

                    <Field label="Devise" required error={formErrors.devise} htmlFor="devise">
                      <select
                        id="devise"
                        ref={(el) => (inputRefs.current.devise = el)}
                        className={`input ${formErrors.devise ? "bad" : ""}`}
                        name="devise"
                        value={formData.devise}
                        onChange={handleChange}
                      >
                        <option value="">Sélectionnez</option>
                        {devises.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field
                    label="Description du projet"
                    required
                    error={formErrors.projetDescription}
                    htmlFor="projetDescription"
                  >
                    <textarea
                      id="projetDescription"
                      ref={(el) => (inputRefs.current.projetDescription = el)}
                      className={`input textarea ${formErrors.projetDescription ? "bad" : ""}`}
                      name="projetDescription"
                      value={formData.projetDescription}
                      onChange={handleChange}
                      placeholder="Expliquez brièvement votre demande…"
                      rows={5}
                    />
                  </Field>
                </Collapsible>

                <Collapsible
                  title="Pièce d'identité"
                  right={<Badge tone="muted">Obligatoire</Badge>}
                  open={openSections.identity}
                  onToggle={() => toggleSection("identity")}
                >
                  <div className="grid2">
                    <UploadCard
                      id="idcard1"
                      title="Photo 1 (recto)"
                      hint="Cliquez pour importer"
                      file={files.idcard1}
                      previewUrl={previews.idcard1}
                      error={formErrors.idcard1}
                      onPick={(e) => handleFileChange(e, "idcard1")}
                      onRemove={() => handleRemoveFile("idcard1")}
                    />
                    <UploadCard
                      id="idcard2"
                      title="Photo 2 (verso — optionnel)"
                      hint="Cliquez pour importer"
                      file={files.idcard2}
                      previewUrl={previews.idcard2}
                      error={formErrors.idcard2}
                      onPick={(e) => handleFileChange(e, "idcard2")}
                      onRemove={() => handleRemoveFile("idcard2")}
                    />
                  </div>
                </Collapsible>

                <Collapsible
                  title="Justificatif de domicile"
                  right={<Badge tone="muted">Obligatoire</Badge>}
                  open={openSections.homeproof}
                  onToggle={() => toggleSection("homeproof")}
                >
                  <div className="grid2">
                    <UploadCard
                      id="homeproof1"
                      title="Photo 1"
                      hint="Cliquez pour importer"
                      file={files.homeproof1}
                      previewUrl={previews.homeproof1}
                      error={formErrors.homeproof1}
                      onPick={(e) => handleFileChange(e, "homeproof1")}
                      onRemove={() => handleRemoveFile("homeproof1")}
                    />
                    <UploadCard
                      id="homeproof2"
                      title="Photo 2 (optionnel)"
                      hint="Cliquez pour importer"
                      file={files.homeproof2}
                      previewUrl={previews.homeproof2}
                      error={formErrors.homeproof2}
                      onPick={(e) => handleFileChange(e, "homeproof2")}
                      onRemove={() => handleRemoveFile("homeproof2")}
                    />
                  </div>
                </Collapsible>
              </div>
            )}

            {/* ================= STEP 3 ================= */}
            {currentStep === 3 && (
              <div className="step stepIn">
                <Collapsible
                  title="Mot de passe"
                  right={<Badge tone="muted">Obligatoire</Badge>}
                  open={openSections.password}
                  onToggle={() => toggleSection("password")}
                >
                  <Field label="Mot de passe" required error={formErrors.mdp} htmlFor="mdp">
                    <div className="passwordWrap">
                      <input
                        id="mdp"
                        ref={(el) => (inputRefs.current.mdp = el)}
                        className={`input ${formErrors.mdp ? "bad" : ""}`}
                        type={showPassword ? "text" : "password"}
                        name="mdp"
                        value={formData.mdp}
                        onChange={handleChange}
                        placeholder="Minimum 6 caractères"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="eyeBtn"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      >
                        {showPassword ? <EyeOffSvg /> : <EyeSvg />}
                      </button>
                    </div>
                    <div className="hint">Votre mot de passe est stocké de manière sécurisée.</div>
                  </Field>
                </Collapsible>

                <Collapsible
                  title="Récapitulatif"
                  right={<Badge tone="brand">Vérifier</Badge>}
                  open={openSections.recap}
                  onToggle={() => toggleSection("recap")}
                >
                  <div className="recap">
                    <div className="recapHead">
                      <div className="recapIcon">
                        <StarsSvg />
                      </div>
                      <div>
                        <div className="recapTitle">Récapitulatif</div>
                        <div className="recapSub">Vérifiez avant d’envoyer.</div>
                      </div>
                    </div>

                    <div className="recapGrid">
                      <div className="recapItem">
                        <span className="recapKey">Nom complet</span>
                        <span className="recapVal">
                          {formData.prenom || "—"} {formData.nom || ""}
                        </span>
                      </div>
                      <div className="recapItem">
                        <span className="recapKey">Email</span>
                        <span className="recapVal">{formData.email || "—"}</span>
                      </div>
                      <div className="recapItem">
                        <span className="recapKey">Localisation</span>
                        <span className="recapVal">
                          {formData.ville ? `${formData.ville}, ` : ""}
                          {formData.pays || "—"}
                        </span>
                      </div>
                      <div className="recapItem">
                        <span className="recapKey">Type d'aide</span>
                        <span className="recapVal">{formData.typeAide || "—"}</span>
                      </div>
                      <div className="recapItem">
                        <span className="recapKey">Montant</span>
                        <span className="recapVal">
                          {formData.montantDemande || "—"} {formData.devise || ""}
                        </span>
                      </div>
                      <div className="recapItem">
                        <span className="recapKey">Documents</span>
                        <span className="recapVal">
                          {[files.idcard1, files.idcard2, files.homeproof1, files.homeproof2].filter(Boolean).length} fichier(s)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="secureNote">
                    <ShieldSvg />
                    <span>
                      En soumettant, vous acceptez le traitement sécurisé de vos informations conformément à la politique de confidentialité.
                    </span>
                  </div>
                </Collapsible>
              </div>
            )}

            <div className="actions">
              {currentStep > 1 ? (
                <button type="button" className="btn ghost" onClick={handlePrevious}>
                  <ArrowLeftSvg />
                  Précédent
                </button>
              ) : (
                <div />
              )}

              {currentStep < 3 ? (
                <button type="button" className="btn primary" onClick={handleNext}>
                  Suivant
                  <ArrowRightSvg />
                </button>
              ) : (
                <button type="submit" className="btn primary" disabled={loading}>
                  {loading ? (
                    <>
                      <SpinnerSvg />
                      Envoi…
                    </>
                  ) : (
                    <>
                      <CheckSvg />
                      Soumettre
                    </>
                  )}
                </button>
              )}
            </div>

            {erreur ? <div className="errorGlobal">{erreur}</div> : null}

            <div className="foot">
              Vous avez déjà un compte ?{" "}
              <a href="/login" className="footLink">
                Se connecter
              </a>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

/* =========================================================
   CSS (TON CSS + mini ajouts pour Collapsible + fix label)
========================================================= */

const css = `
:root{
  --loginBg:#1e293b;
  --panel: rgba(255,255,255,0.08);
  --panel2: rgba(255,255,255,0.10);
  --card: rgba(255,255,255,0.96);
  --card2: rgba(248,250,252,0.96);
  --text:#0f172a;
  --muted:#64748b;
  --muted2:#94a3b8;
  --line: rgba(255,255,255,0.14);
  --lineStrong: rgba(15,23,42,0.14);
  --brand:#2563eb;
  --brand2:#1d4ed8;
  --brandSoft: rgba(37,99,235,.14);
  --danger:#e11d48;
  --dangerSoft: rgba(225,29,72,.10);
  --radius:18px;
  --shadow: 0 22px 60px rgba(0,0,0,.28);
  --shadow2: 0 14px 36px rgba(0,0,0,.20);
}

*{ box-sizing:border-box; }
html, body{ height:100%; }
body{
  margin:0;
  background: var(--loginBg);
  color: var(--text);
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
}

.page{
  position:relative;
  min-height: 100vh;
  padding: 22px 14px;
  overflow:hidden;
}

.loginBg{
  position:absolute;
  inset:0;
  background: radial-gradient(900px 600px at 15% 15%, rgba(99,102,241,.18), transparent 60%),
              radial-gradient(900px 600px at 90% 10%, rgba(124,58,237,.16), transparent 55%),
              radial-gradient(900px 700px at 70% 90%, rgba(56,189,248,.10), transparent 55%),
              var(--loginBg);
  pointer-events:none;
  z-index: 0;
}

.shell{
  position:relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  display:grid;
  grid-template-columns: 1.05fr .95fr;
  gap: 18px;
  align-items: stretch;
}
@media (max-width: 980px){
  .shell{ grid-template-columns: 1fr; gap: 14px; }
}

/* LEFT HERO */
.hero{
  border-radius: var(--radius);
  background: linear-gradient(180deg, var(--panel), rgba(255,255,255,.05));
  border: 1px solid var(--line);
  box-shadow: var(--shadow2);
  overflow:hidden;
  position:relative;
  padding: 20px;
  color: rgba(255,255,255,.92);
}
.heroTop{ padding: 6px 4px 10px; }
.logo{ display:flex; align-items:center; gap:10px; }
.logoMark{
  width:40px; height:40px;
  border-radius: 12px;
  background: rgba(99,102,241,.18);
  border:1px solid rgba(255,255,255,.18);
  display:flex; align-items:center; justify-content:center;
}
.logoText{ font-weight: 800; letter-spacing:.2px; color:#fff; }

.heroKicker{ margin-top: 14px; font-size: 13px; color: rgba(255,255,255,.72); }
.heroTitle{ margin: 8px 0 0; font-size: 28px; line-height: 1.15; font-weight: 800; color:#fff; }
.heroSub{ margin: 10px 0 0; color: rgba(255,255,255,.74); line-height: 1.55; font-size: 14px; font-weight: 450; }

.heroSteps{ display:flex; gap:10px; padding: 14px 4px 2px; flex-wrap:wrap; }
.stepPill{
  display:flex; align-items:center; gap:10px;
  padding: 10px 12px;
  border-radius: 999px;
  background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.14);
  color: rgba(255,255,255,.78);
  transition: transform .15s ease, border-color .15s ease, background .15s ease;
}
.stepPillIcon{ width:22px; height:22px; display:flex; align-items:center; justify-content:center; }
.stepPillLabel{ font-size: 13px; font-weight: 700; }
.stepPill.active{
  background: rgba(99,102,241,.22);
  border-color: rgba(255,255,255,.22);
  color: #fff;
  transform: translateY(-1px);
}

.heroCard{
  margin-top: 12px;
  border-radius: 16px;
  background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.14);
  padding: 14px;
}
.heroCardHeader{ display:flex; gap:12px; align-items:flex-start; }
.heroCardIcon{
  width:40px; height:40px; border-radius: 12px;
  background: rgba(99,102,241,.20);
  border: 1px solid rgba(255,255,255,.16);
  display:flex; align-items:center; justify-content:center;
}
.heroCardTitle{ font-weight: 800; font-size: 14px; color:#fff; }
.heroCardText{ font-size: 13px; margin-top: 2px; color: rgba(255,255,255,.72); line-height:1.45; }

.heroStats{
  margin-top: 12px;
  display:grid;
  grid-template-columns: repeat(3,1fr);
  gap: 10px;
}
.mini{
  border-radius: 14px;
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.12);
  padding: 10px;
  text-align:center;
}
.miniValue{ font-weight: 900; color:#fff; }
.miniLabel{ margin-top: 2px; font-size: 12px; color: rgba(255,255,255,.70); }

.heroImage{ margin-top: 14px; padding: 2px 4px 4px; }
.heroImageFrame{
  border-radius: 18px;
  overflow:hidden;
  border:1px solid rgba(255,255,255,.16);
  background: rgba(255,255,255,.06);
}
.heroImg{ width:100%; height: 220px; object-fit: cover; display:block; }
.heroImgFallback{
  height:220px;
  padding:16px;
  display:flex;
  flex-direction:column;
  justify-content:center;
  gap:8px;
  color: rgba(255,255,255,.90);
}
.heroImgFallback code{
  background: rgba(0,0,0,.25);
  padding: 2px 6px;
  border-radius: 8px;
}

/* RIGHT FORM */
.card{
  border-radius: var(--radius);
  background: rgba(255,255,255,.96);
  border: 1px solid rgba(255,255,255,.12);
  box-shadow: var(--shadow);
  overflow:hidden;
}
.cardHeader{ padding: 18px 18px 0; }

.progress{
  display:flex; align-items:flex-start; justify-content:space-between; gap:10px;
  margin-bottom: 14px;
}
.pStep{ flex:1; position:relative; display:flex; flex-direction:column; align-items:center; gap:6px; }
.pLine{
  position:absolute;
  top: 23px;
  left: 50%;
  width: 100%;
  height: 4px;
  background: rgba(15,23,42,.12);
  transform: translateX(44px);
  border-radius: 999px;
  z-index: 0;
}
.pLine.on{ background: rgba(37,99,235,.28); }

.pDot{
  width: 46px; height: 46px; border-radius: 999px;
  background: rgba(15,23,42,.06);
  border: 2px solid rgba(15,23,42,.16);
  display:flex; align-items:center; justify-content:center;
  font-weight: 900;
  color: rgba(15,23,42,.72);
  transition: all .18s ease;
  z-index: 2;
  box-shadow: 0 10px 22px rgba(0,0,0,.08);
}
.pDot.on{
  background: rgba(37,99,235,.14);
  border-color: rgba(37,99,235,.55);
  color: rgba(15,23,42,.92);
}
.pDot.done{
  background: rgba(37,99,235,.95);
  border-color: rgba(37,99,235,.95);
  color:#fff;
}
.pDot.active{
  box-shadow: 0 0 0 6px rgba(37,99,235,.12), 0 10px 22px rgba(0,0,0,.10);
}

.pLabel{
  font-size: 11px;
  color: rgba(15,23,42,.55);
  text-align:center;
  max-width: 160px;
  line-height:1.2;
  font-weight: 600;
  z-index: 2;
}
.pLabel.on{ color: rgba(15,23,42,.70); }

.titleRow{
  display:flex; align-items:center; justify-content:space-between; gap:10px;
  padding: 6px 0 10px;
}
.stepTitle{ margin:0; font-size: 18px; font-weight: 900; color: rgba(15,23,42,.95); }
.divider{ height:1px; background: rgba(15,23,42,.10); margin-bottom: 10px; }

.form{ padding: 12px 18px 18px; }
.step{ display:flex; flex-direction:column; gap: 12px; }
.stepIn{ animation: stepIn .22s ease both; }
@keyframes stepIn{ from{ opacity:0; transform: translateY(6px); } to{ opacity:1; transform: translateY(0); } }

.grid2{ display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 640px){ .grid2{ grid-template-columns: 1fr; } }

.field{ display:flex; flex-direction:column; gap: 8px; }
.fieldTop{ display:flex; justify-content:space-between; align-items:center; gap:10px; }

.label{ font-size: 13px; font-weight: 650; color: rgba(15,23,42,.70); }
.req{ color: var(--danger); font-weight: 900; }
.errorTiny{ font-size: 12px; color: var(--danger); font-weight: 650; }

.input{
  width:100%;
  padding: 12px 12px;
  border-radius: 12px;
  border: 1px solid rgba(15,23,42,.14);
  background: rgba(248,250,252,.92);
  color: var(--text);
  outline:none;
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease, background .15s ease;
}
.input:focus{
  border-color: rgba(37,99,235,.55);
  box-shadow: 0 0 0 4px rgba(37,99,235,.14);
  transform: translateY(-1px);
  background: #fff;
}
.input.bad{
  border-color: rgba(225,29,72,.55);
  box-shadow: 0 0 0 4px rgba(225,29,72,.12);
}
.textarea{ resize: vertical; min-height: 120px; }

.hint{ font-size: 12px; color: rgba(15,23,42,.55); }

.input::placeholder,
textarea::placeholder{
  color: rgba(100,116,139,.90) !important;
  opacity: 1 !important;
}

select.input{ color: var(--text) !important; background:#fff; }
select.input option{ background:#fff; color: var(--text); }

.section{
  margin-top: 6px;
  padding: 14px;
  border-radius: 16px;
  background: rgba(248,250,252,.96);
  border: 1px solid rgba(15,23,42,.12);
}

.badge{
  display:inline-flex; align-items:center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15,23,42,.12);
  font-size: 12px;
  font-weight: 800;
  color: rgba(15,23,42,.65);
  background: rgba(15,23,42,.04);
}
.badge--brand{
  background: rgba(37,99,235,.14);
  border-color: rgba(37,99,235,.18);
  color: rgba(15,23,42,.92);
}
.badge--muted{
  background: rgba(15,23,42,.04);
  border-color: rgba(15,23,42,.12);
  color: rgba(15,23,42,.62);
}

.uploadCard{
  border-radius: 16px;
  border: 1px solid rgba(15,23,42,.12);
  background: #fff;
  overflow:hidden;
}
.uploadCard--error{ border-color: rgba(225,29,72,.35); }

.dropzone{
  display:flex;
  flex-direction:column;
  gap: 12px;
  padding: 14px;
  cursor:pointer;
  transition: transform .15s ease, background .15s ease;
}
.dropzone:hover{ transform: translateY(-1px); background: rgba(15,23,42,.03); }

.dropTop{ display:flex; align-items:center; gap: 10px; }
.dropIcon{
  width:44px; height:44px; border-radius: 14px;
  background: rgba(37,99,235,.10);
  border: 1px solid rgba(37,99,235,.18);
  display:flex; align-items:center; justify-content:center;
}
.uploadTitle{ font-weight: 850; }
.uploadHint{ font-size: 12px; color: rgba(15,23,42,.60); }
.dropFooter{ display:flex; gap: 8px; flex-wrap:wrap; }

.previewImg{ width:100%; height: 170px; object-fit: cover; display:block; }
.previewMeta{
  padding: 10px 14px 0;
  display:flex; justify-content:space-between; gap:10px;
}
.previewName{
  font-size: 12px;
  font-weight: 800;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
.previewSize{ font-size: 12px; color: rgba(15,23,42,.55); }

.removeBtn{
  margin: 10px 14px 14px;
  display:flex; align-items:center; justify-content:center; gap:8px;
  border-radius: 12px;
  padding: 10px 12px;
  cursor:pointer;
  background: var(--dangerSoft);
  border: 1px solid rgba(225,29,72,.18);
  color: var(--danger);
  font-weight: 800;
  transition: transform .15s ease, background .15s ease;
}
.removeBtn:hover{ transform: translateY(-1px); background: rgba(225,29,72,.12); }

.errorBlock{
  padding: 10px 14px 14px;
  color: var(--danger);
  font-size: 12px;
  font-weight: 650;
}

.passwordWrap{ position:relative; }
.eyeBtn{
  position:absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border:none;
  cursor:pointer;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(15,23,42,.06);
  border: 1px solid rgba(15,23,42,.12);
  color: rgba(15,23,42,.60);
  display:flex; align-items:center; justify-content:center;
}
.eyeBtn:hover{ background: rgba(15,23,42,.08); }

.recap{
  border-radius: 16px;
  background: rgba(37,99,235,.08);
  border: 1px solid rgba(37,99,235,.16);
  padding: 14px;
}
.recapHead{ display:flex; gap: 10px; align-items:center; margin-bottom: 10px; }
.recapIcon{
  width:42px; height:42px; border-radius: 14px;
  background: rgba(37,99,235,.12);
  border: 1px solid rgba(37,99,235,.16);
  display:flex; align-items:center; justify-content:center;
}
.recapTitle{ font-weight: 900; }
.recapSub{ font-size: 12px; color: rgba(15,23,42,.60); margin-top: 2px; }

.recapGrid{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
@media (max-width: 640px){ .recapGrid{ grid-template-columns: 1fr; } }
.recapItem{
  background: rgba(255,255,255,.96);
  border: 1px solid rgba(15,23,42,.12);
  border-radius: 14px;
  padding: 10px 12px;
  display:flex; flex-direction:column; gap:4px;
}
.recapKey{ font-size: 12px; color: rgba(15,23,42,.60); font-weight: 650; }
.recapVal{ font-size: 13px; font-weight: 850; }

.secureNote{
  margin-top: 8px;
  padding: 12px 12px;
  border-radius: 14px;
  background: rgba(34,197,94,.09);
  border: 1px solid rgba(34,197,94,.18);
  display:flex; gap:10px; align-items:center;
  color: rgba(15,23,42,.70);
  font-size: 12px;
  line-height:1.35;
}

.actions{
  margin-top: 14px;
  display:flex;
  gap: 10px;
  align-items:center;
  justify-content:space-between;
}
.btn{
  display:flex;
  align-items:center;
  justify-content:center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(15,23,42,.14);
  cursor:pointer;
  font-weight: 850;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
  background: rgba(255,255,255,.92);
  min-width: 150px;
}
.btn:hover{ transform: translateY(-1px); box-shadow: 0 12px 24px rgba(0,0,0,.12); }

.btn.primary{
  background: #0f172a;
  border-color: transparent;
  color: #fff;
}
.btn.primary:hover{ background: #0b1220; }

.btn.ghost{ background: rgba(15,23,42,.06); }
.btn:disabled{ opacity:.65; cursor:not-allowed; transform:none; box-shadow:none; }

.errorGlobal{
  margin-top: 12px;
  padding: 12px 12px;
  border-radius: 12px;
  background: var(--dangerSoft);
  border: 1px solid rgba(225,29,72,.20);
  color: var(--danger);
  font-weight: 850;
  text-align:center;
}

.foot{
  margin-top: 14px;
  font-size: 13px;
  color: rgba(15,23,42,.65);
  text-align:center;
}
.footLink{
  color: var(--brand);
  text-decoration:none;
  font-weight: 900;
}
.footLink:hover{ text-decoration: underline; }

@keyframes spin { to { transform: rotate(360deg); } }

/* ✅ Collapsible styles (ajout) */
.collapsible{
  border-radius: 16px;
  border: 1px solid rgba(15,23,42,.12);
  background: rgba(248,250,252,.96);
  overflow:hidden;
}
.collapsibleBtn{
  width:100%;
  background: transparent;
  border: none;
  padding: 12px 12px;
  cursor: pointer;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 10px;
}
.collapsibleBtn:hover{ background: rgba(15,23,42,.03); }
.collapsibleLeft{ display:flex; align-items:center; gap: 10px; }
.chev{
  width: 22px;
  height: 22px;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius: 10px;
  background: rgba(15,23,42,.06);
  border: 1px solid rgba(15,23,42,.10);
  font-weight: 900;
}
.collapsibleTitle{ font-weight: 900; color: rgba(15,23,42,.92); }
.collapsibleRight{ display:flex; align-items:center; gap: 8px; }
.collapsibleBody{ padding: 12px 12px 14px; border-top: 1px solid rgba(15,23,42,.10); }
`;

/* =========================================================
   SVG ICONS
========================================================= */

function UploadSvg() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M12 15V3" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 8l5-5 5 5" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 15v3a3 3 0 003 3h10a3 3 0 003-3v-3" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function TrashSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M3 6h18" stroke="#e11d48" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="#e11d48" strokeWidth="2" strokeLinecap="round" />
      <path d="M6 6l1 16a2 2 0 002 2h6a2 2 0 002-2l1-16" stroke="#e11d48" strokeWidth="2" strokeLinecap="round" />
      <path d="M10 11v6M14 11v6" stroke="#fb7185" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function BoltSvg() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8Z" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function WaveSvg() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path
        d="M2 12c2.2 0 2.2-4 4.4-4s2.2 4 4.4 4 2.2-4 4.4-4 2.2 4 4.4 4 2.2-4 4.4-4"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.9"
      />
    </svg>
  );
}

function UserSvg() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M12 12a4 4 0 100-8 4 4 0 000 8Z" stroke="#ffffff" strokeWidth="2" />
      <path d="M4 22a8 8 0 0116 0" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
    </svg>
  );
}

function DocSvg() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path
        d="M7 3h7l3 3v15a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2Z"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M14 3v4h4" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round" opacity="0.9" />
      <path d="M8 12h8M8 16h8" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
    </svg>
  );
}

function ShieldSvg() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M12 2l8 4v6c0 5-3.2 9.4-8 10-4.8-.6-8-5-8-10V6l8-4Z" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-5" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EyeSvg() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" stroke="#0f172a" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 15a3 3 0 100-6 3 3 0 000 6Z" stroke="#1d4ed8" strokeWidth="2" />
    </svg>
  );
}

function EyeOffSvg() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M3 3l18 18" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" />
      <path d="M10.5 10.5a2.5 2.5 0 003.5 3.5" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" />
      <path d="M6.3 6.3C3.8 8.2 2 12 2 12s3.5 7 10 7c2.1 0 3.9-.5 5.4-1.3" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" />
      <path d="M9.9 5.1C10.6 5 11.3 5 12 5c6.5 0 10 7 10 7s-1.1 2.2-3.2 4" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function StarsSvg() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2Z" stroke="#1d4ed8" strokeWidth="2" strokeLinejoin="round" />
      <path d="M19 14l.7 2.6L22 17.3l-2.3.7L19 20.6l-.7-2.6L16 17.3l2.3-.7L19 14Z" stroke="#0f172a" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowLeftSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M15 18l-6-6 6-6" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowRightSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M9 6l6 6-6 6" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M20 6L9 17l-5-5" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SpinnerSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }}>
      <path d="M12 2a10 10 0 1010 10" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

export default MultiStepForm;
